import { useEffect, useMemo, useState } from 'react';
import { modalKeys } from '@ping/constants/modal-keys.constant';
import { useSubmitReviewRequestCompliance } from '@ping/api';
import { t } from '@ping/helpers';
import { BASE_URL } from '@ping/configs';
import { useMobile, useRemoteModal } from '@ping/hooks';
import { Button, Modal, Text, Toast } from '@ping/uikit';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import clsx from 'clsx';
import { tokenStore } from '@ping/stores/token.store';
import { pendingForReviewModalRemoteStateSelector, useModalStore } from '@ping/stores/modal.store';

import 'filepond/dist/filepond.min.css';
import style from './style.module.scss';
import { userInformationStore } from '@ping/stores/userInformation.store';

registerPlugin(FilePondPluginFileValidateType);
registerPlugin(FilePondPluginFileValidateSize);

const DOCUMENTS_LIST = [
  'Bank statement',
  'Pay stubs',
  'Tax returns',
  'Sale of property documents',
  'Inheritance documents',
  'Loan agreements',
  'Business or investment income documents',
  'Retirement or pension statements',
  'Divorce settlement or alimony documents',
  'Personal savings or investments statements',
] as const;

export const UploadDocumentsModal = () => {
  const isMobile = useMobile();
  const modal = useRemoteModal(modalKeys.UPLOAD_DOCUMENTS);
  const [files, setFiles] = useState([]);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const pendingForReviewModal = useModalStore(pendingForReviewModalRemoteStateSelector);
  const { isLoading: isSubmitComplianceLoading } = useSubmitReviewRequestCompliance({
    query: {
      enabled: isSubmitClicked,
      onSuccess: () => {
        setIsSubmitClicked(false);
        pendingForReviewModal.open();
        modal.state.close();
        userInformationStore.changeLastRequestToPending();
      },
      onError: () => {
        setIsSubmitClicked(false);
      },
    },
  });

  const uploadAreaLabel = useMemo(() => {
    if (isMobile) {
      return `
        <img class="upload-label--icon" src="/img/download.svg" alt="" />
        <span class="upload-label--content">
          <span class="upload-label--title">${t('Upload a document')}</span>
          <span class="upload-label--text">${t('click to browse')} ${t('(5MB max)')}</span>
        </span>
     `;
    }
    return `
      <img class="upload-label--image" src="/img/upload-illustration.svg" alt="" />
      <span class="upload-label--title">${t('Drag document here')}</span>
      <span class="upload-label--text">${t('or')} <span class="has-underline">${t('click to browse')}
      </span> ${t('(5MB max)')}</span>
     `;
  }, [isMobile]);

  const handleSubmitUploadForm = () => {
    setIsSubmitClicked(true);
  };

  useEffect(() => {
    if (isMobile) {
      const filePondWrapper = document.querySelector('.filepond--wrapper') as HTMLElement;
      const filePondList = document.querySelector('.filepond--list-scroller') as HTMLElement;
      const filePondHeight = `${(files.length + 1) * 75}px`;
      setTimeout(() => {
        if (filePondWrapper) filePondWrapper.style.height = filePondHeight;
        if (filePondList) filePondList.style.height = filePondHeight;
      }, 100);
    }
  }, [files]);

  useEffect(() => {
    if (!modal.state.isOpen) {
      setFiles([]);
    }
  }, [modal.state.isOpen]);

  return (
    <Modal.Root {...modal.props}>
      <Modal.Portal className={style['upload-documents-modal']}>
        <Modal.Header className={style['upload-documents-modal__header']}>
          <Modal.Title>{t('Provide source of funds')}</Modal.Title>
          <Modal.CloseButton className={style['upload-documents-modal__close']} />
        </Modal.Header>
        <Modal.Content className={style['upload-documents-modal__content']}>
          <div className={style['upload-documents-modal__details']}>
            <Text body='semi-bold'>{t('Supported documents')}</Text>
            <ul className={style['documents-list']}>
              {DOCUMENTS_LIST.map(documentItem => (
                <li key={documentItem} className={style['document-item']}>
                  {documentItem}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={clsx(style['upload-documents-modal__uploadarea'], {
              [style['has-file']]: files.length,
            })}
          >
            <FilePond
              files={files}
              onupdatefiles={setFiles}
              allowMultiple={true}
              // TODO: handling upload flow using our react-query hooks
              server={{
                process: {
                  url: `${BASE_URL}/frontoffice/api/v1/file-transfer/upload?documentType=ProofOfFunds`,
                  headers: {
                    authorization: `Bearer ${tokenStore.getAccessToken()}`,
                  },
                  ondata: formData => {
                    const newFormData = new FormData();
                    newFormData.append('', formData.getAll('documents')[1]);
                    return newFormData;
                  },
                  onerror: errorData => {
                    if (errorData) {
                      Toast.error({ title: JSON.parse(errorData)?.errors?.[0]?.message });
                    }
                  },
                },
              }}
              name='documents'
              acceptedFileTypes={['image/jpg', 'image/jpeg', 'image/gif', 'image/png', 'application/pdf']}
              allowSyncAcceptAttribute
              labelIdle={uploadAreaLabel}
              credits={false}
              iconDone={`<img src='/img/upload-file.svg' alt='' />`}
              maxFileSize='5MB'
              allowFileSizeValidation
            />
          </div>
        </Modal.Content>
        <Modal.Actions className={style['upload-documents-modal__actions']}>
          <Button size='large' uppercase onClick={handleSubmitUploadForm} isLoading={isSubmitComplianceLoading}>
            {t('Submit')}
          </Button>
        </Modal.Actions>
      </Modal.Portal>
    </Modal.Root>
  );
};
